/**
 * 商談 (deals) の CRUD hooks。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §3.4, §6
 *
 * - 一覧読込は Firestore 直接 (read 権限のみ)
 * - 書込は Cloud Function Callable 経由 (Phase 2 では transitionDealStage / mergeDeals / splitDeal /
 *   markDealWon / markDealLost / updateDeal)
 */
import type {
  Deal,
  DealActivity,
  DealCategory,
  DealStage,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface DealRecord extends Deal {
  id: string;
}

export interface DealActivityRecord extends DealActivity {
  id: string;
}

// ─────────────────────────────────────────────
// Query
// ─────────────────────────────────────────────

interface UseDealsOptions {
  /** contactId で絞り込み (顧客詳細の商談タブ用) */
  contactId?: string;
  /** soft delete されたものを含めるか (Phase 9 ゴミ箱用) */
  includeDeleted?: boolean;
}

export function useDeals(options?: UseDealsOptions) {
  return useQuery({
    queryKey: ['deals', options ?? null],
    queryFn: async (): Promise<DealRecord[]> => {
      const constraints = [];
      if (options?.contactId) {
        constraints.push(where('contactId', '==', options.contactId));
      }
      if (!options?.includeDeleted) {
        constraints.push(where('_deleted', '==', false));
      }
      constraints.push(orderBy('lastTouchedAt', 'desc'));

      const q = query(collection(db, 'deals'), ...constraints);
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Deal) }));
    },
  });
}

export function useDeal(id: string | null | undefined) {
  return useQuery({
    queryKey: ['deals', 'detail', id],
    enabled: !!id,
    queryFn: async (): Promise<DealRecord | null> => {
      if (!id) return null;
      const snap = await getDoc(doc(db, 'deals', id));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Deal) };
    },
  });
}

export function useDealActivities(dealId: string | null | undefined) {
  return useQuery({
    queryKey: ['deals', dealId, 'activities'],
    enabled: !!dealId,
    queryFn: async (): Promise<DealActivityRecord[]> => {
      if (!dealId) return [];
      const snap = await getDocs(
        query(
          collection(db, 'deals', dealId, 'activities'),
          orderBy('createdAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as DealActivity) }));
    },
  });
}

// ─────────────────────────────────────────────
// Mutations (Callable)
// ─────────────────────────────────────────────

const createDealFn = httpsCallable<
  {
    contactId: string;
    contactKind: 'btob' | 'btoc';
    title: string;
    category: DealCategory;
    stage: DealStage;
    primaryCategoryId?: string | null;
    expectedAmount?: number | null;
    assigneeUid?: string | null;
    memo?: string;
  },
  { id: string }
>(functions, 'createDeal');

const transitionDealStageFn = httpsCallable<
  { dealId: string; toStage: DealStage },
  { id: string; stage: DealStage }
>(functions, 'transitionDealStage');

const updateDealFn = httpsCallable<
  {
    id: string;
    title?: string;
    memo?: string;
    assigneeUid?: string | null;
    expectedAmount?: number | null;
    primaryCategoryId?: string | null;
    /** T3: 紹介者 contactId */
    referrerContactId?: string | null;
    /** T3: SA (営業パートナー) admins/{uid} */
    salesAgentUid?: string | null;
    /** T3: 親 (所有者) contactId */
    parentContactId?: string | null;
  },
  { id: string }
>(functions, 'updateDeal');

const markDealWonFn = httpsCallable<
  {
    dealId: string;
    finalAmount: number;
    memo?: string;
    wonQuoteId?: string | null;
  },
  { id: string }
>(functions, 'markDealWon');

const markDealLostFn = httpsCallable<
  {
    dealId: string;
    lostReason: string;
    memo?: string;
    lostQuoteId?: string | null;
  },
  { id: string }
>(functions, 'markDealLost');

const mergeDealsFn = httpsCallable<
  { srcDealId: string; dstDealId: string },
  { dstId: string }
>(functions, 'mergeDeals');

const splitDealFn = httpsCallable<
  { srcDealId: string; activityIds: string[]; newTitle?: string },
  { newDealId: string }
>(functions, 'splitDeal');

export function useCreateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      contactId: string;
      contactKind: 'btob' | 'btoc';
      title: string;
      category: DealCategory;
      stage: DealStage;
      primaryCategoryId?: string | null;
      expectedAmount?: number | null;
      assigneeUid?: string | null;
      memo?: string;
    }) => {
      const res = await createDealFn(input);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', { contactId: vars.contactId }] });
    },
  });
}

export function useTransitionDealStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { dealId: string; toStage: DealStage }) => {
      const res = await transitionDealStageFn(input);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals', vars.dealId, 'activities'] });
    },
  });
}

export function useUpdateDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      memo?: string;
      assigneeUid?: string | null;
      expectedAmount?: number | null;
      primaryCategoryId?: string | null;
      referrerContactId?: string | null;
      salesAgentUid?: string | null;
      parentContactId?: string | null;
      isCompanyReferral?: boolean;
    }) => {
      const res = await updateDealFn(input);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.id] });
    },
  });
}

export function useMarkDealWon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      finalAmount: number;
      memo?: string;
      wonQuoteId?: string | null;
    }) => {
      const res = await markDealWonFn(input);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals', vars.dealId, 'activities'] });
      qc.invalidateQueries({ queryKey: ['mail_contacts'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useMarkDealLost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      dealId: string;
      lostReason: string;
      memo?: string;
      lostQuoteId?: string | null;
    }) => {
      const res = await markDealLostFn(input);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dealId] });
      qc.invalidateQueries({ queryKey: ['deals', vars.dealId, 'activities'] });
    },
  });
}

export function useMergeDeals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { srcDealId: string; dstDealId: string }) => {
      const res = await mergeDealsFn(input);
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'detail', vars.dstDealId] });
      qc.invalidateQueries({
        queryKey: ['deals', vars.dstDealId, 'activities'],
      });
    },
  });
}

export function useSplitDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      srcDealId: string;
      activityIds: string[];
      newTitle?: string;
    }) => {
      const res = await splitDealFn(input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}
